import {
  ChevronDown,
  ChevronUp,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Import app store images
import appGalleryImg from "@/assets/footer/appgallery.png";
import appStoreImg from "@/assets/footer/appstore.png";
import playStoreImg from "@/assets/footer/playtore.png";
import {
  fetchAppDownloadSection,
  fetchTransportAppDownloadSection,
} from "@/services/landing/hareengEatsService";
import {
  fetchSocialLinks,
  SocialLink,
} from "@/services/landing/socialLinksService";

const footerLinks = [
  {
    links: [
      { label: "Taxi Rides", path: "/pages/catch-rides" },
      { label: "Scooters", path: "/pages/find-scooters" },
      { label: "Restaurants", path: "/pages/restaurants" },
      // { label: "Hareeng Eats", path: "/pages/eats" },
      { label: "Hareeng Business", path: "/coming-soon" },
    ],
    title: "Services",
  },
  {
    links: [
      { label: "Drive with Hareeng", path: "/driver/vehicle-driver-signup" },
      { label: "Deliver with Hareeng", path: "/driver/delivery-signup" },
      // {
      //   label: "Sell on Hareeng Eats",
      //   path: "/partners/restaurant-registration",
      // },
      { label: "Fleet Owners", path: "/partners/fleet-registration" },
      {
        label: "Courier Opportunities",
        path: "/partners/courier-registration",
      },
    ],
    title: "Partner with Us",
  },
  {
    links: [
      { label: "Who We Are", path: "/pages/about-us/who-we-are" },
      { label: "Careers", path: "/pages/about-us/careers" },
      // { label: "Updates", path: "/pages/about-us/updates" },
      { label: "For Investors", path: "/pages/about-us/for-investors" },
      { label: "For Communities", path: "/pages/about-us/for-communities" },
    ],
    title: "About Us",
  },
  {
    links: [
      { label: "Rider Safety", path: "/safety/rider-safety" },
      { label: "Scooter Safety", path: "/safety/scooter-safety" },
      { label: "Report an Issue", path: "/support/report-issue" },
      { label: "Report a Vehicle", path: "/support/report-vehicle" },
    ],
    title: "Safety & Trust",
  },
  {
    links: [
      { label: "Cities We Serve", path: "/pages/about-us/cities-we-serve" },
      { label: "Airport Services", path: "/pages/about-us/airport-services" },
      { label: "Emergency Support", path: "/support/emergency-report" },
      {label:   "Contact Us", path: "/contact-us" },
    ],
    title: "Where We Operate",
  },
];

const legalLinks = [
  { label: "Terms of Use", path: "/pages/legal/terms-of-use" },
  { label: "Privacy Policy", path: "/pages/legal/privacy-policy" },
  { label: "Cookie Preferences", path: "/pages/legal/cookie-preferences" },
  { label: "Data Protection", path: "/pages/legal/data-protection" },
];

export default function Footer() {
  const [open, setOpen] = useState<null | number>(null);
  const [socials, setSocials] = useState<SocialLink[]>([]);
  const navigate = useNavigate();

  // App store URLs
  const [androidUrl, setAndroidUrl] = useState<string>("");
  const [iosUrl, setIosUrl] = useState<string>("");
  const [appGalleryUrl, setAppGalleryUrl] = useState<string>("");

  useEffect(() => {
    const loadSocialLinks = async () => {
      const links = await fetchSocialLinks();
      setSocials(links.filter((link) => link.is_active));
    };
    loadSocialLinks();
  }, []);

  useEffect(() => {
    Promise.all([
      fetchTransportAppDownloadSection(),
      fetchAppDownloadSection(),
    ]).then(([transportSection]) => {
      if (transportSection) {
        setAndroidUrl(transportSection.androidUrl || "");
        setIosUrl(transportSection.iosUrl || "");
        setAppGalleryUrl(transportSection.appGalleryUrl || "");
      }
    });
  }, []);

  const getSocialIcon = (iconName: string) => {
    // Try to get Lucide icon first
    const LucideIcon = (LucideIcons as any)[iconName];
    if (LucideIcon) {
      return { Icon: LucideIcon, type: "lucide" };
    }
    // Fallback to Font Awesome
    const faIcon = iconName.toLowerCase();
    return { icon: faIcon, type: "fontawesome" };
  };

  const handleLinkClick = (path: string) => {
    // Check if it's an external link or internal route
    if (path.startsWith("http")) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      navigate(path);
    }
  };

  const handleAppDownload = (type: "eats" | "rider") => {
    if (type === "rider") {
      navigate("/pages/catch-rides");
    } else {
      navigate("/pages/eats");
    }
  };

  return (
    <footer className="bg-base-300 px-4 pb-4 pt-12 text-base-content">
      {/* Desktop */}
      <div className="mx-auto hidden max-w-7xl grid-cols-2 gap-8 border-b border-base-content/20 pb-8 md:grid lg:grid-cols-5">
        {footerLinks.map((section) => (
          <div key={section.title}>
            <div className="mb-2 font-bold">{section.title}</div>
            <ul className="space-y-1">
              {section.links.map((link) => (
                <li key={link.label}>
                  <button
                    className="text-left text-base-content/80 transition-colors hover:text-primary hover:underline"
                    onClick={() => handleLinkClick(link.path)}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Mobile Collapsible */}
      <div className="border-b border-base-content/20 pb-4 md:hidden">
        {footerLinks.map((section, idx) => (
          <div className="mb-2" key={section.title}>
            <button
              className="flex w-full items-center justify-between py-2 font-bold"
              onClick={() => setOpen(open === idx ? null : idx)}
            >
              {section.title}
              {open === idx ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              )}
            </button>
            {open === idx && (
              <ul className="space-y-1 pb-2 pl-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <button
                      className="text-left text-base-content/80 transition-colors hover:text-primary hover:underline"
                      onClick={() => handleLinkClick(link.path)}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Bottom bar */}
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-6 pt-6 md:flex-row md:justify-between">
        {/* Language and Branding - Hidden on Mobile */}
        <div className="hidden items-center gap-4 md:flex">
          <span className="flex items-center gap-2">
            <img
              alt="Lesotho"
              className="h-4 w-6 rounded"
              src="https://flagpedia.net/data/flags/w580/ls.webp"
            />
            EN
          </span>
          <button
            className="ml-4 text-xl font-bold transition-colors hover:text-primary"
            onClick={() => navigate("/")}
          >
            Hareeng
          </button>

          {/* Socials */}
          <div className="ml-4 flex gap-3">
            {socials.map((social) => {
              const iconData = getSocialIcon(social.icon);
              return (
                <a
                  aria-label={social.name}
                  className="text-lg transition-colors hover:text-primary"
                  href={social.url}
                  key={social.id}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {iconData.type === "lucide" ? (
                    <iconData.Icon size={18} />
                  ) : (
                    <i className={`fa-brands fa-${iconData.icon}`}></i>
                  )}
                </a>
              );
            })}
          </div>
        </div>

        {/* App Store Images */}
        <div className="grid w-full grid-cols-2 gap-3 md:flex md:w-auto md:flex-row md:gap-4">
          {/* Play Store */}
          <div className="flex justify-center">
            {androidUrl ? (
              <a
                className="block"
                href={androidUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img
                  alt="Get it on Google Play Store"
                  className="h-auto w-full max-w-[140px] md:max-w-[160px]"
                  src={playStoreImg}
                />
              </a>
            ) : (
              <div className="block opacity-50 cursor-not-allowed">
                <img
                  alt="Get it on Google Play Store"
                  className="h-auto w-full max-w-[140px] md:max-w-[160px]"
                  src={playStoreImg}
                />
              </div>
            )}
          </div>

          {/* App Gallery */}
          <div className="flex justify-center">
            {appGalleryUrl ? (
              <a
                className="block"
                href={appGalleryUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img
                  alt="Explore it from AppGallery"
                  className="h-auto w-full max-w-[140px] md:max-w-[160px]"
                  src={appGalleryImg}
                />
              </a>
            ) : (
              <div className="block opacity-50 cursor-not-allowed">
                <img
                  alt="Explore it from AppGallery"
                  className="h-auto w-full max-w-[140px] md:max-w-[160px]"
                  src={appGalleryImg}
                />
              </div>
            )}
          </div>

          {/* App Store - Full width on mobile, normal width on desktop */}
          <div className="col-span-2 flex justify-center md:col-span-1">
            {iosUrl ? (
              <a
                className="block"
                href={iosUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img
                  alt="Download it from App Store"
                  className="h-auto w-full max-w-[140px] md:max-w-[160px]"
                  src={appStoreImg}
                />
              </a>
            ) : (
              <div className="block opacity-50 cursor-not-allowed">
                <img
                  alt="Download it from App Store"
                  className="h-auto w-full max-w-[140px] md:max-w-[160px]"
                  src={appStoreImg}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Language and Branding - Mobile */}
      <div className="mt-6 flex items-center justify-center gap-4 md:hidden">
        <span className="flex items-center gap-2">
          <img
            alt="Lesotho"
            className="h-4 w-6 rounded"
            src="https://flagpedia.net/data/flags/w580/ls.webp"
          />
          EN
        </span>
        <button
          className="text-lg font-bold transition-colors hover:text-primary"
          onClick={() => navigate("/")}
        >
          Hareeng
        </button>

        {/* Socials Mobile */}
        <div className="flex gap-3">
          {socials.map((social) => {
            const iconData = getSocialIcon(social.icon);
            return (
              <a
                aria-label={social.name}
                className="text-lg transition-colors hover:text-primary"
                href={social.url}
                key={social.id}
                rel="noopener noreferrer"
                target="_blank"
              >
                {iconData.type === "lucide" ? (
                  <iconData.Icon size={18} />
                ) : (
                  <i className={`fa-brands fa-${iconData.icon}`}></i>
                )}
              </a>
            );
          })}
        </div>
      </div>

      {/* Legal Section */}
      <div className="mx-auto mt-6 flex max-w-7xl flex-col items-center justify-center gap-2 text-xs text-base-content/60 md:justify-between md:flex-row">
        <div className="flex flex-wrap justify-center gap-4 md:justify-start">
          {legalLinks.map((link) => (
            <button
              className="transition-colors hover:text-primary"
              key={link.label}
              onClick={() => handleLinkClick(link.path)}
            >
              {link.label}
            </button>
          ))}
        </div>
        <div className="mt-2 text-center md:mt-0 md:text-right">
          &copy; {new Date().getFullYear()} Hareeng Mobile Technology . Designed by Bokang Mothomotsoana
        </div>
      </div>
    </footer>
  );
}
